import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

export type AccentColor = 'emerald' | 'amber' | 'indigo' | 'rose';

export const ACCENT_COLORS: Record<AccentColor, {
  border: string;
  icon: string;
  // gradientFrom must be 6-digit hex (no alpha, no rgb/hsl)
  gradientFrom: string;
}> = {
  emerald: { border: 'border-emerald-500', icon: 'text-emerald-400', gradientFrom: '#10b981' },
  amber:   { border: 'border-amber-500',   icon: 'text-amber-400',   gradientFrom: '#f59e0b' },
  indigo:  { border: 'border-indigo-500',  icon: 'text-indigo-400',  gradientFrom: '#6366f1' },
  rose:    { border: 'border-rose-500',    icon: 'text-rose-400',    gradientFrom: '#f43f5e' },
};

interface WidgetHeaderProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  accentColor: AccentColor;
  actions?: React.ReactNode;
  compact?: boolean;
  hero?: boolean;
}

const WidgetHeader: React.FC<WidgetHeaderProps> = ({
  icon, title, subtitle, accentColor, actions, compact = false, hero = false,
}) => {
  const { darkMode } = useTheme();
  const colors = ACCENT_COLORS[accentColor];

  // Gradient opacity: hero > standard > none (compact)
  const opacity = hero
    ? (darkMode ? '26' : '33')   // ~15% dark, ~20% light
    : (darkMode ? '1a' : '21');  // ~10% dark, ~13% light

  const bgStyle = compact
    ? undefined
    : { background: `linear-gradient(to right, ${colors.gradientFrom}${opacity}, transparent)` };

  return (
    <div
      className={compact
        ? 'flex items-center gap-2 mb-1.5'
        : 'flex items-center gap-2 px-1 py-1.5 -mx-1 -mt-1 mb-2 rounded'
      }
      style={bgStyle}
    >
      {/* Icon */}
      <span className="shrink-0">{icon}</span>

      {/* Title */}
      <span className={`${compact ? 'text-xs' : 'text-sm'} font-semibold truncate ${
        darkMode ? 'text-gray-200' : 'text-gray-800'
      }`}>
        {title}
      </span>

      {/* Subtitle + Actions — right aligned */}
      <span className="ml-auto flex items-center gap-2 shrink-0">
        {subtitle && (
          <span className={`${compact ? 'text-[10px]' : 'text-xs'} font-mono text-gray-500`}>
            {subtitle}
          </span>
        )}
        {actions}
      </span>
    </div>
  );
};

export default WidgetHeader;
